import {
  CuboidProps,
  CurrentImageProps,
  Number3,
  PointCloudInfo,
  SphericalImageProps,
} from '@abyss/3d-viewer';
import { ReactElement, ReactNode, SetStateAction } from 'react';
import { SxProps } from '@mui/material';
import { IconButtonProps } from '@mui/material/IconButton';
import { ExtractSubjectType, MongoQuery, Subject, SubjectRawRule } from '@casl/ability';
import {
  FileNameTypes,
  GetStructureLocationsForInspectionDataLoaderQuery,
  PointOfInterestsFragment,
  PointOfInterestTemplateTypeEnum,
  SeenByViewpoint,
  UnitsEnum,
  GetAssemblyPointOfInterestsQuery,
  PointOfInterest,
  CorrosionLevel,
  AllPointsOfInterestForInspectionDataLoaderQuery,
  Template,
  PointOfInterestTemplate,
  DefectLegend,
  GetAllPointOfInterestTemplatesQuery,
  AllPointOfInterestsForDataLoaderQuery,
  SplitCuboid,
  AllAssemblyTemplatesForFilterSideBarQuery,
  PointOfInterestsQuery,
  GetAllStructuresListForBreadcrumbQuery,
  GetAssembliesByTemplateFieldsFiltersForAitInsightsQuery,
  GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery as AssembliesDataQuery,
  PointOfInterest as PointOfInterestWithCuboidsType,
  Maybe,
  Vector3,
  User,
  AllStructuresForPlatformsQuery,
  StructureStatus,
  GetAnalysisStructureLocationsQuery,
  Version,
  PaintRegionGroupsQuery,
  DefectsAreaForCustomBlocksQuery,
  File,
  OptionData,
  UnitSystemEnum,
  TemplateFieldTypeEnum,
  PoiStatus,
  ImageType,
} from '@/__generated__/graphql';
import { StateMap } from '@/utils/stateMap';
import { GraphQLError } from 'graphql';
import { TableData } from './table';
import { CuboidBase } from '@/modules/cuboids/types';

export { TemplateFieldTypeEnum } from '@/__generated__/graphql';
export type PartSummary = {
  id: string;
  assemblyId?: string | null;
  assemblyTagName?: string;
  partClassName: string;
  isHidden: boolean | undefined | null;
  isReviewed?: boolean | null;
};
export type PointOfInterestChecked = PointOfInterest & { checked?: boolean };
export type AssemblyPointOfInterestQuery = GetAssemblyPointOfInterestsQuery['allPointOfInterests'];
export type PointOfInterestQuery =
  AllPointsOfInterestForInspectionDataLoaderQuery['allPointOfInterests'];
export type DataLoaderPointOfInterestQueryObject =
  AllPointOfInterestsForDataLoaderQuery['pointOfInterests'];
export type PointOfInterestsQueryType = PointOfInterestsQuery['pointOfInterests'];
export type PlatformsType = AllStructuresForPlatformsQuery['allStructures'];

export type PointOfInterestDocument = PointOfInterest;
export type PointOfInterestTemplateDocument = PointOfInterestTemplate;

export type AllAssembliesByTemplateFilter =
  GetAssembliesByTemplateFieldsFiltersForAitInsightsQuery['getAssembliesByTemplateFieldsFilters'];
export type AllPointOfInterestTemplate =
  GetAllPointOfInterestTemplatesQuery['allPointOfInterestTemplate'];
export type TemplateDocument = Template;
export type AssemblyTemplatesQuery = AllAssemblyTemplatesForFilterSideBarQuery['allTemplates'];
export type AssemblyTemplateQuery = AllAssemblyTemplatesForFilterSideBarQuery['allTemplates'][0];
export type EquipmentCardType = AssemblyTemplateQuery['equipmentCard'];
export type AllStructures = GetAllStructuresListForBreadcrumbQuery['allStructures'];
export type PartId = string;
export type AnnotationId = string;
export type AssemblyId = string;
export type FocalPoint = Vector3;
export type PartInfo = {
  annotationIds: Set<AnnotationId>;
  class: string;
  assemblyId?: string | null;
  isHidden?: boolean | undefined | null;
  // Key for hiding unhide labeling classes, will be updated
  // to isHidden once isHidden will be renamed to isOutOfScope
  isHiddenVolatile?: boolean | undefined | null;
  isReviewed?: boolean | null;
};

export type AnnotationInfo = {
  // Need this to map an annotation id to what 3d viewer is aware of,
  // e.g. when we need to change an annotation's color
  annotation3dReference: number;
  partId: string;
  locationId?: string | null;
  localAnnotationId?: number;
  id?: string;
};

export type AssemblyInfo = {
  id?: string;
  tagName: string;
  originalTagname?: string;
  superCategory?: string;
  isNewlyAdded?: boolean | undefined;
  __typename?: string;
  structure?: {
    id: string;
    __typename?: string;
  };
};

export type AssemblyCameraPose = {
  position: Vector3;
  lookAt: Vector3;
};

export type Abilities = SubjectRawRule<string, ExtractSubjectType<Subject>, MongoQuery>[];

export type StructureRelationships = {
  // Annotation 3d reference is what the 3d viewer knows about
  // we need this map to translate from 3d viewer annotation to
  // the database annotation id
  // e.g. when handling a click event on the 3d viewer point cloud
  byAnnotation3dReference: StateMap<number, AnnotationId>;
  // Annotation id maps to the database annotation id
  byAnnotationId: StateMap<AnnotationId, AnnotationInfo>;
  byPartId: StateMap<PartId, PartInfo>;
  byAssemblyId: StateMap<AssemblyId, AssemblyInfo>;
};

export type AnalysisStructureRelationships = {
  annotationIdToAssemblyId: {
    [annotationId: string]: string;
  };
  annotationIdToAnnotation3d: {
    [annotationId: string]: number;
  };
  annotation3dToAnnotationId: {
    [annotation3d: number]: string;
  };
  assemblyIdToAnnotation3dArray: {
    [assemblyId: string]: number[];
  };
  assemblyIdAndLocationIdToAnnotation3dArray: {
    [assemblyId: string]: {
      [locationId: string]: number[];
    };
  };
  assemblyIdToAssembly3d: {
    [assemblyId: string]: number;
  };
  partsByAnnotationId: {
    [partId: string]: number[];
  };
  parts: {
    [partId: string]: { annotations: number[]; class: string };
  };
  loaded?: boolean;
};

export type SnackbarMessage = {
  shouldShow: boolean;
  content?: React.ReactElement;
  persistent?: boolean;
  duration?: number;
};

export type AbyssViewerMode = 'Normal' | 'Spherical' | 'IsolatePart' | 'VoxelSelector';

export type AbyssViewerState = {
  mode: AbyssViewerMode;
  imageViewpoint?: SphericalImageProps;
  previousMode?: AbyssViewerMode;
  previousImageViewpoint?: SphericalImageProps;
};

export type SphericalPointCloudState = 'Loaded' | 'Loading' | 'Error';

export type SphericalPointCloudMode = 'All' | 'Related' | 'None';

export type NormalVisibilityRange = 'All' | 'ZoneOnly' | 'Custom';

export type Colors = {
  colors: number[];
  selectedColour: number;
  noAssemblyColour: number;
  outOfScopeColour: number;
  highlightReviewedPartColour: number;
  segmentsFillColour: number;
  segmentsLineColour: number;
  foundAssemblyColour: number;
  hoveredAssemblyColour: number;
};

export type IsolatedPartVisibilityRangeType = 'All' | 'Selected' | 'Unselected';

export type Point3d = {
  x: number;
  y: number;
  z: number;
};

export type Point2d = {
  x: number;
  y: number;
};

export type BlisterProfilePlotPoint = {
  x: number;
  y: number;
  z: number;
  height: number;
};

export type PanelsMode =
  | 'Default'
  | 'IsolatePart'
  | 'MeasureBlister'
  | 'AssetDetail'
  | 'SidePanelOff'
  | 'Comments';

export type Viewpoint = {
  distanceDisplayText?: string | null;
} & SeenByViewpoint;

export enum HeatmapValueEnum {
  CORROSION = 'substrateConditionMax',
  COATINGCONDITION = 'coatingConditionMax',
  TRACE = 'trace',
  DEFECTS = 'defects',
}
export type HeatmapValue = 'substrateConditionMax' | 'coatingConditionMax' | 'trace' | 'defects';

export type Border3d = {
  points: Point3d[];
};

export type AddBlisterState =
  | 'Inactive'
  | 'WaitingForCenter'
  | 'WaitingForEdge'
  | 'Loading'
  | 'Reviewing'
  | 'UpdatingBlister'
  | undefined;

export type BlisterToAdd =
  | {
      state?: AddBlisterState;
      centerPoint?: Point2d;
      edgePoint?: Point2d;
    }
  | undefined;

export type PointOfInterestState =
  | 'Inactive'
  | 'WaitingForPoint'
  | 'WaitingForDetail'
  | 'WaitingForTransition';
export type PoiFormState =
  | 'Create'
  | 'Verify'
  | 'Update-General'
  | 'Update-Template'
  | 'Update-Location';

export type EditPointOfInterest = {
  state: PointOfInterestState;
  template?: AllPointOfInterestTemplate[0];
  pointOfInterest?: PointOfInterest | undefined;
  formState?: PoiFormState;
  hasBlisterMeasurement?: boolean;
  isManuallyCreated?: boolean;
  isAutoGenerated?: boolean;
  poiSphericalId?: string | undefined;
  customBlockId?: string | undefined;
};

/**
 *  A single object structure that reside in byAnnotation3dReference
 *  eg: {
 *          12: {
 *              supervoxelId: '1sdasd123sadedfdfe0',
 *              supervoxelClassId: '1sdasd123sadedfdfe1',
 *              color: '#AEFT123'
 *              highlightColor: '#AEFT123',
 *              isSelected: false
 *            }
 *      }
 */
export type ByAnnotation3dReferenceEntity = {
  supervoxelId: string;
  supervoxelClassId: string;
  color: string;
  highlightColor: number;
  isSelected: boolean;
};

/**
 *  A single object structure  that reside in bySupervoxelClassId
 *  eg: {
 *          '1sdasd123sadedfdfe1': {
 *              id: '1sdasd123sadedfdfe1',
 *              zoneId: '1sdasd123sadedfdfe2'
 *              color: '#AEFT123',
 *              name: 'flange',
 *              annotationIds: [1,3,4,5,7,8,9,10],
 *              highlightColor: '#AEFT123',
 *              isSelected: false
 *            }
 *      }
 */
export type BySupervoxelClassIdEntity = {
  id: string;
  zoneId: string;
  color: string;
  name: string;
  annotationIds: number[];
  highlightColor: number;
  isSelected: boolean;
};

export type LabellingStructureRelationships = {
  // Annotation 3d reference is what the 3d viewer knows about
  // we need this map to translate from 3d viewer annotation to
  // the database annotation id
  // e.g. when handling a click event on the 3d viewer point cloud
  byAnnotation3dReference: {
    [annotation3dReference: number]: ByAnnotation3dReferenceEntity;
  };

  // grouping by class is benefical when performing hide/unhide
  // labelling classes and evaluation which voxel has particular class
  bySupervoxelClassId: {
    [supervoxelClassId: string]: BySupervoxelClassIdEntity;
  };
};

// default type color for highligh, de-selected etc.
// eg: While performing Multi-Select the box has yellow color.
//     While performing De-Select the box has white color etc.
export type LabellingColors = {
  highlightColor: number;
  deselectColor: number;
  defaultColor: number;
  selectedColour?: number;
};

// descibes the different coloring mode in labelling
export enum LabellingColorMode {
  SuperVoxel,
  SuperVoxelClass,
  Scan,
}

export enum LabellingBlendingMode {
  SUPERVOXEL = 'superVoxel',
  SUPERVOXEL_RGB = 'superVoxel_RGB',
  COLOUR = 'color',
}

export enum LabellingAnnotationMode {
  SUPERVOXEL = 'superVoxel',
  ASSET_TYPE = 'assetType',
}

export enum SelectedTab {
  COMPONENT,
  POINTS_OF_INTEREST,
  PAINT_BLOCK,
  DECKS,
  COMMENTS,
  CUSTOM_BLOCKS,
}

export type ToggleableDefectLegend = {
  isEnabled: boolean;
} & DefectLegend;

export type WidgetData = {
  id: string;
  displayName: string;
  type: PointOfInterestTemplateTypeEnum;
  icon: ReactElement<'svg'>;
  size: IconButtonProps['size'];
  color: IconButtonProps['color'];
};

export type AnalysisDrawerLeft = {
  visibility: boolean;
  query: boolean;
};
export type AnalysisDrawerRight = {
  equipment: boolean;
  viewpoints: boolean;
  measuringTool: boolean;
  findViewpointsTool: boolean;
};

export type DrawersProps = {
  isOpen: boolean;
  handleOpen: () => void;
  allClosed: boolean;
};

export type GroupByResult<T> = { [key: string]: T[] };

export type PointOfInterestGroupBy = PointOfInterestChecked & {
  templateId?: Maybe<string> | undefined;
};

export type HandleCreateReportParameters = {
  reportName: string;
  pointOfInterestIds: Array<string>;
};

export type HandleAddPOIToReportParameters = {
  reportId: string;
  pointOfInterestIds: Array<string>;
};

export type GenericModalProps = {
  open: boolean;
  handleClose: () => void;
  children?: ReactNode;
  title?: string;
  description?: string;
  sx?: SxProps | undefined;
  crossIconStyle?: SxProps | undefined;
  headerStyleProp?: SxProps | undefined;
  backgroundPaperStyle?: SxProps | undefined;
  titleSx?: SxProps | undefined;
};

export type GenericAccordionProps = {
  summary: string;
  summaryStyles?: SxProps | undefined;
  children: React.ReactNode;
  numericIndicator?: number;
  openByDefault?: boolean;
  customSummaryChip?: React.ReactNode;
  summaryIcon?: React.ReactNode;
};

export type PoiAccordionProps = {
  summary: string;
  numericIndicator?: number;
  openByDefault?: boolean;
  groupedPOI: PointOfInterestGroupBy[];
  setSelectedPointOfInterests: React.Dispatch<
    SetStateAction<
      Array<{
        id: string;
        name: string;
      }>
    >
  >;
  selectedPointOfInterests:
    | Array<{
        id: string;
        name: string;
      }>
    | [];
};
export type WindowSize = {
  width: number | undefined;
  height: number | undefined;
};

export type FilterState = {
  query: {
    status?: string | null;
    serviceGroup?: string | null;
    assetType?: string | null;
    assetIdentifier?: string | null;
    corrosionLevel: CorrosionLevel[];
  };
  isQueryExpanded: boolean;
  isResultsExpanded: boolean;
};

type ColumnIconType<T extends TableData = TableData> = {
  icon: JSX.Element;
  action: string;
  condition?: (rows: T[]) => boolean;
  dialogTitle?: string | null | undefined;
  dialogContent?: string | null | undefined;
  tooltipText?: string | null | undefined;
};

export type Columns = {
  isSortable: boolean;
  sortType?: 'NUMBER' | 'STRING';
  sortKey?: string;
  visibility: boolean;
  key: string;
  columnName: string;
  displayConfig?: {
    type: string;
    color?: Color[];
  };
  onCellClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: TableData) => void;
  templateFieldKey?: string;
  isTemplateField?: boolean;
  displayInQuery?: boolean | null | undefined;
  displayInRisk?: boolean | null | undefined;
  roundToDecimal?: number | null | undefined;
  unit?: string | null | undefined;
  displayUnit?: string | null | undefined;
  columnIcon?: ColumnIconType | undefined;
  optionsData?: Maybe<Array<OptionData>> | undefined;
  isBlisterField?: boolean | undefined;
};

export type PlatformColumn = {
  key: string;
  columnName: string;
  visibility: boolean;
  width: string;
};

type RegionData = {
  id: string;
  name: string;
  structures: StructureData[];
  [key: string]: number | string | null | undefined | StructureData[];
};

type StructureData = {
  id: string;
  name: string;
  status: StructureStatus;
  decks: DeckData[];
  [key: string]: number | string | null | undefined | DeckData[];
};

type DeckData = {
  id: string;
  name: string;
  [key: string]: number | string | null | undefined;
};

export type PlatformProps = {
  columns: PlatformColumn[];
  handlePlatformChange: (id: string, status: StructureStatus) => void;
  handleFetchStructureDecks: (id: string) => void;
  searchPlatform: string;
  structureDecksLoading: boolean;
};

export type ListViewTableProps = {
  regions: RegionData[];
  loading: boolean;
} & PlatformProps;

export type RegionDataProps = {
  region: RegionData;
} & PlatformProps;

export type StructureDataProps = {
  structure: StructureData;
  setExpandedRows: React.Dispatch<React.SetStateAction<{}>>;
  expandedRows: { [key: string]: boolean };
} & PlatformProps;

export type Color = {
  key: string;
  value: string;
} | null;

export type SortType = 'parts' | 'points' | 'sphericals';
export type SortDirection = 'asc' | 'desc';
export type SortableField = `${SortType}-${SortDirection}` | undefined;

export enum TabLabel {
  MEMBER_TAB = 0,
  PLATFORM_TAB = 1,
}

export enum RolesTabLabel {
  MEMBERS_TAB = 0,
  PERMISSIONS_TAB = 1,
}

export type InviteUsersState = {
  emails: string[];
  roles: string[];
  groups: string[];
};

export type TemplateWithPOIs = {
  id: string;
  name: string;
  type: PointOfInterestTemplateTypeEnum;
  pointOfInterests: {
    location: {
      cx: number;
      cy: number;
    };
  }[];
};

export enum MeasurementToolState {
  IN_ACTIVE = 'Inactive',
  WAITING_FOR_POINT = 'WaitingForPoint',
  DONE = 'Done',
}
export enum InsightsFeatures {
  COMPONENTS_TAB = 'COMPONENTS_TAB',
  DECK_TAB = 'DECK_TAB',
  POINTS_OF_INTEREST_TAB = 'POINTS_OF_INTEREST_TAB',
  PAINT_BLOCK_TAB = 'PAINT_BLOCK_TAB',
}
export enum ViewerFeatures {
  DECK_FILTER = 'DECK_FILTER',
  PAINT_BLOCK_FILTER = 'PAINT_BLOCK_FILTER',
}

export type StructureFeatureFlags = {
  insights?: InsightsFeatures[];
  viewer?: ViewerFeatures[];
};

export enum Permissions {
  EXPORT_ASSET = 'export:asset',
  UPDATE_ASSET = 'update:asset',
  READ_DEFECTS = 'read:defects',
  MANAGE_PAINT_REGIONS = 'manage:paint_regions',
  MANAGE_USERS = 'manage:users',
  MANAGE_BETA_FEATURES = 'manage:beta_features',
  CREATE_CUPS_POI = 'create:cups_poi',
  CREATE_HOTBOLTING_POI = 'create:hotbolting_poi',
  CREATE_BLISTER_POI = 'create:blister_poi',
  CREATE_PAINT_DAMAGE_POI = 'create:paint_damage_poi',
  UPDATE_CUPS_POI = 'update:cups_poi',
  UPDATE_HOTBOLTING_POI = 'update:hotbolting_poi',
  UPDATE_BLISTER_POI = 'update:blister_poi',
  UPDATE_PAINT_DAMAGE_POI = 'update:paint_damage_poi',
  READ_STRUCTURE_LIST_VIEW = 'read:structure_list_view',
  CREATE_UPLOAD_MEDIA = 'create:upload_media',
  MANAGE_TEMPLATE = 'manage:template',
  MANAGE_DEFECTS = 'manage:defects',
  CREATE_ASSEMBLY = 'create:assembly',
  MANAGE_ASSEMBLY_TEMPLATE = 'manage:assembly_template',
  READ_BLISTER_TOOL_RECOMMENDATIONS = 'read:blister_tool_recommendations',
  UPDATE_WALL_LOSS_FACTOR = 'update:wall_loss_factor',
  UPDATE_STRUCTURE_BLISTER_HEIGHT_TYPE = 'update:structure_blister_height_type',
}

export type ReportSubmitState = 'create' | 'edit';
export enum UserManagementTabs {
  AllUsers,
  GROUPS,
  ROLES,
  PLATFORMS,
  TEAMS,
}
export type GroupedChartData = {
  pointOfInterestFindings: { [key: string]: number };
  name: string;
};

export type GroupedDataForCharts = {
  groupedByDeck: {
    [deckName: string]: GroupedChartData & {
      [key: string]: number | string | { [key: string]: number };
    };
  };
  groupedByPaintBlock: {
    [paintBlockName: string]: GroupedChartData & {
      [key: string]: number | string | { [key: string]: number };
    };
  };
};

export type UsersTableHeadCell = {
  disablePadding: boolean;
  id: keyof User;
  label: string;
  numeric: boolean;
};

export type Order = 'asc' | 'desc';

export type AutoCompleteOption = {
  label: string;
  value: string;
};

export type UserRoles =
  | 'admin'
  | 'beta-user'
  | 'asset-tagging-user'
  | 'ait-engineer'
  | 'dev'
  | 'generic-engineer'
  | 'client'
  | 'api-inspector'
  | 'alpha-user'
  | 'es-user'
  | 'mt-application-engineer'
  | 'mt-inspector'
  | 'ait-task-lead'
  | 'pilot-tester'
  | 'fm-engineer'
  | 'mi-engineer'
  | 'work-in-progress';

export type InputAdormantHandlerMap = {
  MEASUREMENT_TOOL: () => void;
  RELOAD: () => void;
  '': () => void;
};

export enum TextFormat {
  NONE = 'NONE',
  UPPER_CASE = 'UPPER_CASE',
  LOWER_CASE = 'LOWER_CASE',
  TITLE_CASE = 'TITLE_CASE',
}

export enum CuboidClickMode {
  Default,
  AddCuboid,
  SelectCuboid,
}

export type CuboidData = Omit<SplitCuboid, 'assembly' | '__typename'>;

export type StyledSplitCuboid = CuboidProps & { cuboidData: CuboidData };

export type LabellingCuboid = CuboidBase & {
  structureId: string;
  zoneId: string;
  cuboidClassId: string;
  isNew?: boolean;
};

export type AnalysisCuboid = CuboidBase & {
  poiId?: string;
  status?: PoiStatus;
};

export type CuboidClass = {
  id: string;
  name: string;
  color: number;
  isVisible?: boolean;
};

export enum CardInsightTypeEnum {
  LINEAR_PROGRESS = 'linearProgress',
  CIRCULAR_PROGRESS = 'circularProgress',
  SIMPLE_TEXT = 'simpleText',
  UNIT_AREA = 'unitArea',
}

export interface LegendColors {
  [key: string]: string;
}

export enum ConditionType {
  COATING = 'COATING',
  SUBSTRATE = 'SUBSTRATE',
}

export type LocationsData = {
  [key: string]: GetAnalysisStructureLocationsQuery['allLocations'];
};

export type AssembliesData = {
  [key: string]: AssembliesDataQuery['getAssembliesByTemplateFieldsFilters'];
};

export type ScreenshotImageObject = {
  id: string;
  viewpointBase64?: string | undefined;
  viewerBase64?: string | undefined;
};

export type ReportImage = {
  id: string;
  base64?: string;
  imageDimensions: { width: number; height: number };
  type?: FileNameTypes;
  coordinates?: { cx: number; cy: number };
  imageType?: ImageType;
  uuid?: string;
};

export enum FindViewpointsToolState {
  INACTIVE = 'Inactive',
  WAITING_FOR_POINT = 'WaitingForPoint',
  WAITING_FOR_QUERY = 'WaitingForQuery',
  DONE = 'Done',
}

export type SelectedManualTemplateTypes = 'BLISTER' | 'PIT';

export type SeenByViewpointsWithLocationAndAnalysis = {
  __typename?: 'SeenByViewpoint' | undefined;
  analysed?:
    | {
        __typename?: 'Analysed' | undefined;
        analysedBy: string;
      }
    | null
    | undefined;
  location: {
    __typename?: 'Location' | undefined;
    id: string;
  };
  focalPoint?: {
    x: number;
    y: number;
    z: number;
  } | null;
};

export enum SocialConnections {
  GOOGLE_OAUTH2 = 'google-oauth2',
  BP_AZURE_AD = 'BP-Azure-AD',
}

export type TemplateTypeWithAllTab =
  | { name: string; type: string; fields: AllPointOfInterestTemplate[0]['fields'] }
  | AllPointOfInterestTemplate[0];

export enum RemediatedStatus {
  IN_PROGRESS = 'In Progress',
  SCHEDULED = 'Scheduled',
  INSPECTION_PENDING = 'Inspection pending',
  NO_WORK_REQUIRED = 'No work required',
  REMEDIATED = 'Remediated',
}

export type PointOfInterestWithCuboids = PointOfInterestWithCuboidsType;

export type FeatureBase = {
  flag: string;
  label?: string;
  disabled?: boolean;
  url?: string;
  permissions?: string[];
  roles?: string[];
  enabled?: boolean;
};

export type Feature = FeatureBase & {
  subNavigation?: FeatureBase[];
};

export type StrucureFeatures = {
  [key: string]: Feature | [string];
};

export type GroupedVersions = {
  [date: string]: Version[];
};

export type NavigationListItem = {
  id: string;
  value: string;
  label: string;
  count?: string | number;
  handleClick: () => void;
};

export enum FilterType {
  MULTI_SELECT = 'MULTI_SELECT',
  SINGLE_SELECT = 'SINGLE_SELECT',
}

export type FilterOption = { key: string; label: string };

export type Filter = {
  key: string;
  label: string;
  type: FilterType;
  options: FilterOption[];
};

export type SelectedFiltersType = Record<string, FilterOption[]>;

export type MappedReportPointOfInterests = PointOfInterestsFragment & {
  tagName: string;
  assemblyId: string;
  pointNumber?: number;
};

export enum UpgradePromptType {
  FULLSCREEN_POPUP = 'FULLSCREEN_POPUP',
  INLINE = 'INLINE',
  INLINE_DROPDOWN = 'INLINE_DROPDOWN',
}

export enum UpgradePromptBackground {
  None = '',
  Reports = 'reports_image.png',
}

export type PointSize = {
  viewer: number;
  spherical: number;
};

export type BlendOpacityModes = {
  viewer: number;
  assembly: number;
  spherical: number;
};
export type GenericUploadFileStateType = {
  state: 'idle' | 'validating' | 'csv-error' | 'generic-error' | 'success' | 'viewing-file';
  error: GraphQLError | undefined;
  inputCsv: string | undefined;
  inputRef: HTMLInputElement | null;
};

// Widgets are based on template fields, so the value represents the name of the template field except for common widgets
export enum POIWidgets {
  Status = 'Status',
  InspectionPriority = 'Inspection Priority',
  DueDate = 'Due Date',
  PaintDamageAlarmLevels = 'Alarm Level',
}

export type ConversionField = {
  unit?: UnitsEnum | undefined;
  isPercentage: boolean;
};

export type ScreenshotLocationType = 'screenshots' | 'attachments';

/**
 * Defines the strategy for rendering tabs.
 *
 * - 'eager': All tabs are rendered immediately and always loaded in the background.
 * - 'lazy': Tabs are rendered only when they are selected. All state is destroyed when a tab is unselected.
 */
export type TabsRenderingStrategy = 'eager' | 'lazy';

export type PaintRegionGroup = PaintRegionGroupsQuery['paintRegionGroups'][number];

/**
 * Creates a type based on `T` where all properties are optional except for the properties specified in `K`.
 *
 * @template T - The base type.
 * @template K - The keys of `T` that should remain required.
 *
 * @example
 * type Example = { a: number; b: string; c: boolean };
 * type Result = PartialExcept<Example, 'a'>;
 * // Result is equivalent to: { a: number; b?: string; c?: boolean }
 */
export type PartialExcept<T, K extends keyof T> = Partial<Omit<T, K>> & Pick<T, K>;

export type AnalysisCustomBlock =
  DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup']['defectsByPaintBlock'][number];

export type AnalysisCustomBlockWithFiles = AnalysisCustomBlock & {
  files?: File[];
};

export enum CloudFrontCookiesKey {
  /** ID of the Cloudfront key pair. */
  KeyPair = 'CloudFront-Key-Pair-Id',
  /** Base64-encoded version of the JSON policy. */
  Policy = 'CloudFront-Policy',
  /** Hashed, signed, and base64-encoded version of the JSON policy. */
  Signature = 'CloudFront-Signature',
  /** The unix date time for when the signed URL or cookie can no longer be accessed. */
  Expires = 'CloudFront-Expires',
}

export enum GcpCdnCookiesKey {
  /** GCP CDN signed cookie containing URLPrefix, Expires, KeyName, and Signature. */
  CloudCdnCookie = 'Cloud-CDN-Cookie',
}

export type CloudfrontSignedCookiesOutput = Record<CloudFrontCookiesKey, string>;
export type GcpCdnSignedCookiesOutput = Record<GcpCdnCookiesKey, string>;
export type SortOption = {
  id: string;
  label: string;
  type?: 'paintBlock' | 'poi';
};

type ViewpointLocation = GetStructureLocationsForInspectionDataLoaderQuery['allLocations'][0];

export type RecommendedViewpointsType = {
  [id: string]: {
    isRecommended: boolean;
    abyssConfidence: string;
    angleFromPoint: number | undefined;
    distanceFromPoint: number | undefined;
    pipeSize: number;
    viewpoint: ViewpointLocation;
  };
};

export type Filters = {
  deckIds?: string[];
  paintRegionIds?: string[];
  templateFilters: { id: string; value: string[] }[];
  workpackIds?: string[];
};

export type LastDeeplinkParameters = {
  query: string;
  selectedSpherical?: CurrentImageProps;
  lookAt?: Number3;
  time: number;
};
export type ScreenshotMode = 'normal';

export type FormatFieldValueParams = {
  unitSystem: UnitSystemEnum;
  type: TemplateFieldTypeEnum | undefined;
  unit?: UnitsEnum | undefined | null;
  value: string;
  showUnitLabel?: boolean;
  isBooleanField?: boolean;
  displayUnit?: UnitsEnum | undefined;
  roundToDecimal?: number | null | undefined;
  isBlisterField?: boolean | undefined;
  isHeight?: boolean | undefined;
  percentage?: number | undefined;
  fallbackLabel?: string | undefined;
};
export type CustomBlockQueryFilters = {
  excludeAlreadyPlannedBlocks: boolean | undefined;
};

export type RefetchCompleteCallback = () => void;

export type AppliedAssemblyFilters = {
  templateFilters: { id: string; value: string[] }[];
  deckFilters: string[];
  paintRegionFilters: string[];
  workpackFilters: string[];
};

export type ImageWithId = {
  src: string;
  id: string;
  name?: string;
  viewpointId?: string;
  isLoading?: boolean;
  imageType?: string;
  caption?: string;
  uuid?: string;
  fileName?: string;
};

export enum FileType {
  GENERAL = 'general',
  ISOMETRIC = 'isometric',
}

export type NavigationItem = {
  id: string;
  name: string;
};

export type ClickEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>;

export type OctreePointCloudsJsonPath = {
  path: string;
  name?: string | string[];
  nodesToShow?: Set<string>;
  onOctreeHierarchyLoaded: (info: PointCloudInfo) => void;
}[];

export type CustomBlockDefects =
  DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup']['defectsByPaintBlock'];

export type VectorisedPoints = {
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
};
export type VectorisedPointsWithHeight = VectorisedPoints & {
  height: number[];
};
